<script setup>
import SvgIcon from '@/components/base/SvgIcon.vue';

const props = defineProps({
  variant: {
    /** 按鈕主題色 */
    // primary | secondary | tertiary | brownish
    type: String,
    // required: true,
    default: 'primary'
  },
  size: {
    /** 按鈕尺寸 */
    // lg | md | sm
    type: String,
    // required: true,
    default: 'md'
  },

  styles: {
    /** 按鈕類型 */
    // filled | outline
    type: String,
    default: 'filled'
  },
  buttonType: {
    type: String,
    default: 'button'
  },
  block: {
    type: Boolean,
    required: false,
    default: false
  },

  disabled: {
    type: Boolean,
    required: false,
    default: false
  },
  rounded: {
    type: Boolean,
    required: false,
    default: false
  },
  labelSize: {
    /** 文字大小 */
    // lg | md | sm
    type: String,
    required: false,
    default: 'md'
  },
  labelStyle: {
    type: String,
    required: false,
    default: 'regular'
  },
  hasIconLeft: {
    /** 有沒有左方icon */
    type: Boolean,
    required: false,
    default: false
  },
  iconLeft: {
    /** 左方icon檔案名稱 */
    type: String
  },
  hasIconRight: {
    /** 有沒有右方icon */
    type: Boolean,
    required: false,
    default: false
  },
  iconRight: {
    /** 右方icon檔案名稱 */
    type: String,
    default: 'arrowRight'
  },
  iconSize: {
    /** icon尺寸 */
    // lg | md | sm
    type: String,
    default: 'md'
  }
});

const classes = computed(() => ({
  [type.value]: true,
  [`${type.value}-${props.variant}-${props.styles}-${props.size}`]: true,
  [`btn-txt-${props.labelSize}-${props.labelStyle}`]: true,
  [`btn-rounded`]: props.rounded,
  [`btn-block`]: props.block,
  [`disabled`]: props.disabled
  // [`btn-disabled`]: props.disabled
}));

const type = computed(() => (props.label ? 'btn' : 'icon'));
</script>
<template>
  <button :class="classes" :type="props.buttonType">
    <!-- <button :class="classes"> -->
    <SvgIcon v-if="props.hasIconLeft" :class="props.iconSize" :name="props.iconLeft" />

    <span>
      <slot></slot>
    </span>
    <SvgIcon v-if="props.hasIconRight" :class="props.iconSize" :name="props.iconRight" />
  </button>
</template>
<style lang="scss" scoped>
$btn-sizes: lg, md, sm; //Button Size

$btn-paddings: (
  sm: 8px 16px,
  md: 8px 24px,
  lg: 12px 24px
); //Button Padding

$btn-font-sizes: (
  'lg': 18,
  'md': 16,
  'sm': 14
); //Button Font Size

$btn-font-weights: (
  'lg': 1.5,
  'md': 1.5,
  'sm': 1.15
); //Button Font Weight

/** Button Border Radius */
$btn-base-border-radius: 4px;
$btn-rounded-border-radius: 100px;

/** ==Primary Button Filled== */
// -Background
$btn-primary-filled-bg-color: $color-primary-100;
$btn-primary-filled-bg-color--hover: $color-primary-darker;
$btn-primary-filled-bg-color--disabled: $color-primary-40;
// -Text
$btn-primary-filled-txt-color: $txt-color-white;
$btn-primary-filled-txt-color--hover: $txt-color-white;
$btn-primary-filled-txt-color--disabled: rgba($txt-color-white, 0.4);
// -Border
$btn-primary-filled-border-color: none;

/** ==Brownish Button Filled== */
// -Background
$btn-brownish-filled-bg-color: #d44934;
$btn-brownish-filled-bg-color--hover: #7f2c1f;
$btn-brownish-filled-bg-color--disabled: $color-primary-40;
// -Text
$btn-brownish-filled-txt-color: $txt-color-white;
$btn-brownish-filled-txt-color--hover: $txt-color-white;
$btn-brownish-filled-txt-color--disabled: rgba($txt-color-white, 0.4);
// -Border
$btn-brownish-filled-border-color: none;

/** ==Primary Button Outline== */
// -Background
// $btn-primary-outline-bg-color: transparent;
$btn-primary-outline-bg-color: $bg-light-100;
$btn-primary-outline-bg-color--hover: $color-primary-20;
$btn-primary-outline-bg-color--disabled: transparent;
// -Text
$btn-primary-outline-txt-color: $color-primary-100;
$btn-primary-outline-txt-color--hover: $color-primary-100;
$btn-primary-outline-txt-color--disabled: $color-primary-40;
// -Border
$btn-primary-outline-border-color: $color-primary-100;
$btn-primary-outline-border-color--disabled: $color-primary-40;

/** ==Secondary Button Filled== */
// -Background
$btn-secondary-filled-bg-color: $bg-dark-300;
$btn-secondary-filled-bg-color--hover: $bg-dark-200;
$btn-secondary-filled-bg-color--disabled: $color-grey-50;
// -Text
$btn-secondary-filled-txt-color: $txt-color-white-100;
$btn-secondary-filled-txt-color--hover: $txt-color-white-100;
$btn-secondary-filled-txt-color--disabled: $txt-color-white-80;
// -Border
$btn-secondary-filled-border-color: none;

/** ==Secondary Button Outline== */
// -Background
$btn-secondary-outline-bg-color: $color-white-base;
$btn-secondary-outline-bg-color--hover: rgba($txt-color-grey, 0.05);
$btn-secondary-outline-bg-color--disabled: transparent;
// -Text
$btn-secondary-outline-txt-color: rgba($txt-color-grey, 0.8);
$btn-secondary-outline-txt-color--hover: rgba($txt-color-grey, 0.8);
$btn-secondary-outline-txt-color--disabled: rgba($txt-color-grey, 0.24);
// -Border
$btn-secondary-outline-border-color: map-get(map-get($colors, black), 10);
$btn-secondary-outline-border-color--disabled: map-get(map-get($colors, black), 05);

/** ==Tertiary Button Filled== */
// -Background
$btn-tertiary-filled-bg-color: $bg-light-300;
$btn-tertiary-filled-bg-color--hover: $color-grey-40;
$btn-tertiary-filled-bg-color--disabled: $bg-light-300;
// -Text
$btn-tertiary-filled-txt-color: $txt-color-grey-80;
$btn-tertiary-filled-txt-color--hover: $txt-color-grey-80;
$btn-tertiary-filled-txt-color--disabled: $txt-color-grey-24;
// -Border
$btn-tertiary-filled-border-color: none;

/** Button Common Setting */
%btn-base {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
  box-sizing: border-box;
  text-decoration: none;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease-in;
}

/** Defined Button Different Status */
$btn-config: (
  brownish-filled: (
    class: 'brownish-filled',
    bg-default: $btn-brownish-filled-bg-color,
    bg-hover: $btn-brownish-filled-bg-color--hover,
    bg-disabled: $btn-brownish-filled-bg-color--disabled,
    color: $btn-brownish-filled-txt-color,
    color--hover: $btn-brownish-filled-txt-color--hover,
    color--disabled: $btn-brownish-filled-txt-color--disabled,
    border: $btn-brownish-filled-border-color,
    border--disabled: $btn-brownish-filled-border-color
  ),
  primary-filled: (
    class: 'primary-filled',
    bg-default: $btn-primary-filled-bg-color,
    bg-hover: $btn-primary-filled-bg-color--hover,
    bg-disabled: $btn-primary-filled-bg-color--disabled,
    color: $btn-primary-filled-txt-color,
    color--hover: $btn-primary-filled-txt-color--hover,
    color--disabled: $btn-primary-filled-txt-color--disabled,
    border: $btn-primary-filled-border-color,
    border--disabled: $btn-primary-filled-border-color
  ),
  secondary-filled: (
    class: 'secondary-filled',
    bg-default: $btn-secondary-filled-bg-color,
    bg-hover: $btn-secondary-filled-bg-color--hover,
    bg-disabled: $btn-secondary-filled-bg-color--disabled,
    color: $btn-secondary-filled-txt-color,
    color--hover: $btn-secondary-filled-txt-color--hover,
    color--disabled: $btn-secondary-filled-txt-color--disabled,
    border: $btn-secondary-filled-border-color,
    border--disabled: $btn-secondary-filled-border-color
  ),
  tertiary-filled: (
    class: 'tertiary-filled',
    bg-default: $btn-tertiary-filled-bg-color,
    bg-hover: $btn-tertiary-filled-bg-color--hover,
    bg-disabled: $btn-tertiary-filled-bg-color--disabled,
    color: $btn-tertiary-filled-txt-color,
    color--hover: $btn-tertiary-filled-txt-color--hover,
    color--disabled: $btn-tertiary-filled-txt-color--disabled,
    border: $btn-tertiary-filled-border-color,
    border--disabled: $btn-tertiary-filled-border-color
  ),
  primary-outline: (
    class: 'primary-outline',
    bg-default: $btn-primary-outline-bg-color,
    bg-hover: $btn-primary-outline-bg-color--hover,
    bg-disabled: $btn-primary-outline-bg-color--disabled,
    color: $btn-primary-outline-txt-color,
    color--hover: $btn-primary-outline-txt-color--hover,
    color--disabled: $btn-primary-outline-txt-color--disabled,
    border: $btn-primary-outline-border-color,
    border--disabled: $btn-primary-outline-border-color--disabled
  ),
  secondary-outline: (
    class: 'secondary-outline',
    bg-default: $btn-secondary-outline-bg-color,
    bg-hover: $btn-secondary-outline-bg-color--hover,
    bg-disabled: $btn-secondary-outline-bg-color--disabled,
    color: $btn-secondary-outline-txt-color,
    color--hover: $btn-secondary-outline-txt-color--hover,
    color--disabled: $btn-secondary-outline-txt-color--disabled,
    border: $btn-secondary-outline-border-color,
    border--disabled: $btn-secondary-outline-border-color--disabled
  )
);

@each $type, $value in $btn-config {
  $class: map-get($value, class);

  $color: map-get($value, color);
  $colorHover: map-get($value, color--hover);
  $colorDisabled: map-get($value, color--disabled);

  $bgColor: map-get($value, bg-default);
  $bgColorHover: map-get($value, bg-hover);
  $bgColorDisabled: map-get($value, bg-disabled);

  $border: map-get($value, border);
  $borderDisabled: map-get($value, border--disabled);

  .btn,
  .icon {
    @extend %btn-base;
    border-radius: $btn-base-border-radius;

    &-rounded {
      border-radius: $btn-rounded-border-radius;
    }

    &-#{$class} {
      @each $btn-size in $btn-sizes {
        &-#{$btn-size} {
          padding: map-get($btn-paddings, $btn-size);
          background: $bgColor;
          color: $color;
          border: 1px solid $border;

          &:hover {
            background: $bgColorHover;
            color: $colorHover;
          }

          // &:disabled {
          //     cursor: not-allowed;
          //     background: $bgColorDisabled;
          //     color: $colorDisabled;
          //     border: 1px solid $borderDisabled;
          // }

          &.disabled {
            background: $bgColorDisabled;
            color: $colorDisabled;
            border: 1px solid $borderDisabled;
          }
        }
      }
    }

    &-txt {
      @each $btn-size, $btn-font-size in $btn-font-sizes {
        &-#{$btn-size}-regular {
          @include txt-style($btn-font-size);
          line-height: map-get($btn-font-weights, $btn-size);
        }

        &-#{$btn-size}-bold {
          @include txt-style($btn-font-size, bold);
          line-height: map-get($btn-font-weights, $btn-size);
        }
      }
    }

    &-block {
      display: block;
      width: 100%;
    }

    &-disabled {
      background: $bgColorDisabled;
      color: $colorDisabled;
      border: 1px solid $borderDisabled;
    }
  }
}

button {
  span {
    white-space: nowrap;
    text-align: center;
    width: 100%;
  }
}
</style>
